import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  UserPlus, Search, ArrowRightLeft, Trash2, Loader2, Award, GraduationCap, KeyRound, Copy,
  Upload, FileSpreadsheet, Download, CheckCircle2, AlertCircle, Info,
} from "lucide-react";
import { toast } from "sonner";
import { UFS } from "@/lib/ufs";

const fmt = (n) => {
  const v = Number(n ?? 0);
  return (Number.isInteger(v) ? v.toFixed(1) : v.toString()).replace(".", ",");
};

const emptyNew = {
  full_name: "", email: "", cpf: "", phone: "", cargo: "", orgao: "", cidade: "", estado: "", profile: "ambos",
};

export default function AdminMatriculas() {
  const [courses, setCourses] = useState([]);
  const [pickCourse, setPickCourse] = useState("");
  const [roster, setRoster] = useState([]);
  const [courseInfo, setCourseInfo] = useState(null);
  const [creditsRequired, setCreditsRequired] = useState(3);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [filter, setFilter] = useState("");

  // Matrícula (dialog)
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollTab, setEnrollTab] = useState("existing");
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [newStudent, setNewStudent] = useState(emptyNew);

  // Senha temporária gerada
  const [tempPwd, setTempPwd] = useState(null); // { name, email, password }

  // Transferência
  const [transferTarget, setTransferTarget] = useState(null); // roster member
  const [transferToCourse, setTransferToCourse] = useState("");
  const [transferring, setTransferring] = useState(false);

  // Remover matrícula
  const [removeTarget, setRemoveTarget] = useState(null);

  // Matrícula em lote (CSV)
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null); // { rows, summary }
  const [csvReport, setCsvReport] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvCommitting, setCsvCommitting] = useState(false);

  const loadCourses = useCallback(() => {
    return api.get("/courses").then((r) => setCourses(r.data || [])).catch(() => {});
  }, []);
  const loadRoster = useCallback((courseId) => {
    if (!courseId) { setRoster([]); setCourseInfo(null); return; }
    setLoadingRoster(true);
    api.get(`/admin/courses/${courseId}/roster`)
      .then((r) => {
        setRoster(r.data?.members || []);
        setCourseInfo(r.data?.course || null);
        setCreditsRequired(r.data?.credits_required ?? 3);
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Erro ao carregar turma"))
      .finally(() => setLoadingRoster(false));
  }, []);

  useEffect(() => { loadCourses(); }, [loadCourses]);
  useEffect(() => { loadRoster(pickCourse); }, [pickCourse, loadRoster]);

  const selectedCourse = useMemo(() => courses.find((c) => c.id === pickCourse), [courses, pickCourse]);

  const filteredRoster = useMemo(() => {
    const t = filter.trim().toLowerCase();
    if (!t) return roster;
    return roster.filter((m) =>
      (m.full_name || "").toLowerCase().includes(t) ||
      (m.email || "").toLowerCase().includes(t) ||
      (m.ceap_id || "").toLowerCase().includes(t)
    );
  }, [roster, filter]);

  const doSearch = async () => {
    if (!searchTerm.trim()) { toast.error("Digite nome, e-mail, CPF ou CEAP ID"); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/users`, { params: { search: searchTerm.trim() } });
      setSearchResults(Array.isArray(data) ? data.slice(0, 25) : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro na busca");
    } finally { setSearching(false); }
  };

  const enrollExisting = async (userId) => {
    setEnrolling(true);
    try {
      const { data } = await api.post(`/admin/courses/${pickCourse}/enroll`, { mode: "existing", user_id: userId });
      if (data.already_enrolled) toast.info("Aluno já estava matriculado nesta turma.");
      else toast.success("Aluno matriculado!");
      setEnrollOpen(false); setSearchResults([]); setSearchTerm("");
      loadRoster(pickCourse);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao matricular");
    } finally { setEnrolling(false); }
  };

  const enrollNew = async () => {
    if (!newStudent.full_name.trim()) { toast.error("Informe o nome completo"); return; }
    if (!newStudent.email.trim()) { toast.error("Informe o e-mail"); return; }
    setEnrolling(true);
    try {
      const { data } = await api.post(`/admin/courses/${pickCourse}/enroll`, { mode: "new", ...newStudent });
      if (data.temp_password) {
        setTempPwd({ name: newStudent.full_name, email: newStudent.email, password: data.temp_password });
      } else if (data.already_enrolled) {
        toast.info("Aluno (já existente) já estava matriculado.");
      } else {
        toast.success("Aluno cadastrado e matriculado!");
      }
      setEnrollOpen(false); setNewStudent(emptyNew);
      loadRoster(pickCourse);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar/matricular");
    } finally { setEnrolling(false); }
  };

  const doTransfer = async () => {
    if (!transferToCourse) { toast.error("Selecione a turma de destino"); return; }
    setTransferring(true);
    try {
      const { data } = await api.post(`/admin/courses/transfer`, {
        user_id: transferTarget.user_id,
        from_course_id: pickCourse,
        to_course_id: transferToCourse,
      });
      toast.success(`Transferido! ${data.attendance_moved} presença(s) movida(s). Créditos no destino: ${fmt(data.credits_in_target)}.`);
      setTransferTarget(null); setTransferToCourse("");
      loadRoster(pickCourse);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao transferir");
    } finally { setTransferring(false); }
  };

  const doRemove = async () => {
    try {
      const { data } = await api.delete(`/admin/courses/${pickCourse}/enroll/${removeTarget.user_id}`);
      if (data.still_has_attendance) {
        toast.warning("Matrícula removida, mas o aluno ainda tem presenças registradas nesta turma. Use Transferir para movê-las.");
      } else {
        toast.success("Matrícula removida.");
      }
      setRemoveTarget(null);
      loadRoster(pickCourse);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover matrícula");
    }
  };

  // ===== Matrícula em lote (CSV) =====
  const downloadCsvTemplate = () => {
    const content = "nome,email,ceap_id\nFulano de Tal,fulano@exemplo.com,\nBeltrano Existente,,CEAP-2026-000123\n";
    const blob = new Blob(["\ufeff" + content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modelo_matricula_lote.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const openCsvDialog = () => {
    setCsvFile(null); setCsvPreview(null); setCsvReport(null);
    setCsvOpen(true);
  };

  const onCsvFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // permite reenviar o mesmo arquivo
    if (!f) return;
    setCsvFile(f); setCsvPreview(null); setCsvReport(null);
    setCsvLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/admin/courses/${pickCourse}/enroll/csv/preview`, fd);
      setCsvPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao ler a planilha");
      setCsvFile(null);
    } finally {
      setCsvLoading(false);
    }
  };

  const doCsvCommit = async () => {
    if (!csvFile) return;
    setCsvCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const { data } = await api.post(`/admin/courses/${pickCourse}/enroll/csv/commit`, fd);
      setCsvReport(data.report);
      const r = data.report;
      toast.success(`Lote processado: ${r.linked} vinculado(s), ${r.created} criado(s), ${r.already} já matriculado(s), ${r.errors} erro(s).`);
      loadRoster(pickCourse);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao processar o lote");
    } finally {
      setCsvCommitting(false);
    }
  };

  const csvStatusBadge = (status) => {
    switch (status) {
      case "to_link": return <Badge className="bg-sky-50 text-sky-800 border border-sky-200">Será vinculado</Badge>;
      case "new_user": return <Badge className="bg-violet-50 text-violet-800 border border-violet-200">Novo — será criado</Badge>;
      case "already_enrolled": return <Badge className="bg-slate-100 text-slate-700 border border-slate-200">Já matriculado</Badge>;
      case "error": return <Badge className="bg-rose-50 text-rose-700 border border-rose-200">Erro</Badge>;
      case "linked": return <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200">Vinculado</Badge>;
      case "created": return <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200">Criado</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const sourceBadge = (source) => {
    if (source === "matricula") return <Badge variant="secondary">Matrícula</Badge>;
    if (source === "presenca") return <Badge className="bg-sky-50 text-sky-800 border border-sky-200">Presença</Badge>;
    return <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200">Matrícula + Presença</Badge>;
  };

  return (
    <AdminLayout title="Matrículas">
      <Card className="rounded-xl mb-4">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <Label>Turma (curso)</Label>
            <Select value={pickCourse} onValueChange={setPickCourse}>
              <SelectTrigger data-testid="matriculas-course-select">
                <SelectValue placeholder="Selecione a turma" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {courses.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`matriculas-course-${c.id}`}>
                    {c.title} — {c.cidade}/{c.estado}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label>Buscar na lista</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Nome, e-mail ou CEAP ID" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="matriculas-filter" />
            </div>
          </div>
          <Button
            onClick={() => { setEnrollOpen(true); setEnrollTab("existing"); setSearchResults([]); setSearchTerm(""); setNewStudent(emptyNew); }}
            disabled={!pickCourse}
            data-testid="matriculas-add-btn"
          >
            <UserPlus className="w-4 h-4 mr-1" /> Matricular aluno
          </Button>
          <Button
            variant="outline"
            onClick={openCsvDialog}
            disabled={!pickCourse}
            data-testid="matriculas-csv-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Matrícula em lote (CSV)
          </Button>
        </CardContent>
      </Card>

      {!pickCourse ? (
        <Card className="rounded-xl">
          <CardContent className="py-16 text-center text-muted-foreground">
            <GraduationCap className="w-10 h-10 mx-auto mb-3" />
            <p>Selecione uma turma para gerenciar as matrículas.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-xl">
          <CardContent className="p-0 overflow-x-auto">
            {loadingRoster ? (
              <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Créditos</TableHead>
                    <TableHead>Certificado</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRoster.map((m) => (
                    <TableRow key={m.user_id} data-testid={`roster-row-${m.user_id}`}>
                      <TableCell>
                        <p className="font-medium">{m.full_name}</p>
                        <p className="text-xs text-muted-foreground">{m.email} · {m.ceap_id}</p>
                      </TableCell>
                      <TableCell>{sourceBadge(m.source)}</TableCell>
                      <TableCell>
                        <Badge className={(m.credits >= creditsRequired)
                          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : "bg-amber-100 text-amber-800 border-amber-200"}>
                          {fmt(m.credits)}/{fmt(creditsRequired)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {m.has_certificate
                          ? <Badge variant="outline" className="capitalize"><Award className="w-3 h-3 mr-1" />{m.certificate_status || "emitido"}</Badge>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" className="mr-1" onClick={() => { setTransferTarget(m); setTransferToCourse(""); }} data-testid={`transfer-${m.user_id}`}>
                          <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Transferir
                        </Button>
                        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setRemoveTarget(m)} data-testid={`remove-${m.user_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredRoster.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">Nenhum aluno nesta turma ainda</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog: matricular */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="max-w-[640px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Matricular aluno</DialogTitle>
            <DialogDescription>{selectedCourse ? selectedCourse.title : ""}</DialogDescription>
          </DialogHeader>
          <Tabs value={enrollTab} onValueChange={setEnrollTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="existing" data-testid="enroll-tab-existing">Aluno existente</TabsTrigger>
              <TabsTrigger value="new" data-testid="enroll-tab-new">Novo aluno</TabsTrigger>
            </TabsList>

            <TabsContent value="existing" className="mt-4 space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Nome, e-mail, CPF ou CEAP ID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  data-testid="enroll-search-input"
                />
                <Button onClick={doSearch} disabled={searching} data-testid="enroll-search-btn">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {searchResults.map((u) => (
                  <div key={u.id} className="flex items-center justify-between border border-[hsl(var(--border))] rounded-lg p-2">
                    <div>
                      <p className="text-sm font-medium">{u.full_name}</p>
                      <p className="text-xs text-muted-foreground">{u.email} · {u.ceap_id}</p>
                    </div>
                    <Button size="sm" onClick={() => enrollExisting(u.id)} disabled={enrolling} data-testid={`enroll-pick-${u.id}`}>
                      <UserPlus className="w-3.5 h-3.5 mr-1" /> Matricular
                    </Button>
                  </div>
                ))}
                {searchResults.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-6">Busque um aluno para matricular.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="new" className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome completo *</Label><Input value={newStudent.full_name} onChange={(e) => setNewStudent((s) => ({ ...s, full_name: e.target.value }))} data-testid="newstudent-name" /></div>
                <div><Label>E-mail *</Label><Input type="email" value={newStudent.email} onChange={(e) => setNewStudent((s) => ({ ...s, email: e.target.value }))} data-testid="newstudent-email" /></div>
                <div><Label>CPF</Label><Input value={newStudent.cpf} onChange={(e) => setNewStudent((s) => ({ ...s, cpf: e.target.value }))} /></div>
                <div><Label>Telefone</Label><Input value={newStudent.phone} onChange={(e) => setNewStudent((s) => ({ ...s, phone: e.target.value }))} /></div>
                <div><Label>Cargo</Label><Input value={newStudent.cargo} onChange={(e) => setNewStudent((s) => ({ ...s, cargo: e.target.value }))} /></div>
                <div><Label>Órgão</Label><Input value={newStudent.orgao} onChange={(e) => setNewStudent((s) => ({ ...s, orgao: e.target.value }))} /></div>
                <div><Label>Cidade</Label><Input value={newStudent.cidade} onChange={(e) => setNewStudent((s) => ({ ...s, cidade: e.target.value }))} /></div>
                <div>
                  <Label>Estado (UF)</Label>
                  <Select value={newStudent.estado} onValueChange={(v) => setNewStudent((s) => ({ ...s, estado: v }))}>
                    <SelectTrigger data-testid="newstudent-estado"><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {UFS.map((uf) => <SelectItem key={uf.value} value={uf.value}>{uf.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Perfil</Label>
                  <Select value={newStudent.profile} onValueChange={(v) => setNewStudent((s) => ({ ...s, profile: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ambos">Ambos</SelectItem>
                      <SelectItem value="legislativo">Legislativo</SelectItem>
                      <SelectItem value="licitacoes">Licitações</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Uma senha temporária será gerada. O aluno deverá trocá-la no primeiro acesso.
              </p>
              <Button className="w-full" onClick={enrollNew} disabled={enrolling} data-testid="newstudent-submit">
                {enrolling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
                Cadastrar e matricular
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Dialog: transferir */}
      <Dialog open={!!transferTarget} onOpenChange={(o) => !o && setTransferTarget(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Transferir aluno</DialogTitle>
            <DialogDescription>
              Transferir <strong>{transferTarget?.full_name}</strong> para outra turma. A presença/créditos
              já registrados ({fmt(transferTarget?.credits)}) vão junto, e o certificado (se houver) passa a valer para a turma de destino.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Turma de destino</Label>
            <Select value={transferToCourse} onValueChange={setTransferToCourse}>
              <SelectTrigger data-testid="transfer-target-select"><SelectValue placeholder="Selecione a turma de destino" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {courses.filter((c) => c.id !== pickCourse).map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`transfer-target-${c.id}`}>{c.title} — {c.cidade}/{c.estado}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTransferTarget(null)}>Cancelar</Button>
            <Button onClick={doTransfer} disabled={transferring} data-testid="transfer-confirm">
              {transferring ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
              Transferir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: remover matrícula */}
      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Remover matrícula</DialogTitle>
            <DialogDescription>
              Remover a matrícula de <strong>{removeTarget?.full_name}</strong> nesta turma?
              As presenças já registradas NÃO serão apagadas (use Transferir para movê-las).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoveTarget(null)}>Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doRemove} data-testid="remove-confirm">
              <Trash2 className="w-4 h-4 mr-1" /> Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: senha temporária */}
      <Dialog open={!!tempPwd} onOpenChange={(o) => !o && setTempPwd(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> Aluno cadastrado</DialogTitle>
            <DialogDescription>
              Anote e envie a senha temporária para <strong>{tempPwd?.name}</strong>. Ela só aparece uma vez.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="rounded-lg border border-[hsl(var(--border))] p-3">
              <p className="text-xs text-muted-foreground">E-mail (login)</p>
              <p className="font-medium">{tempPwd?.email}</p>
            </div>
            <div className="rounded-lg border border-[hsl(var(--border))] p-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Senha temporária</p>
                <p className="font-mono text-lg" data-testid="temp-password-value">{tempPwd?.password}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(tempPwd?.password || ""); toast.success("Senha copiada"); }}>
                <Copy className="w-4 h-4 mr-1" /> Copiar
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPwd(null)} data-testid="temp-password-close">Entendi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: matrícula em lote (CSV) */}
      <Dialog open={csvOpen} onOpenChange={(o) => { setCsvOpen(o); if (!o) { setCsvFile(null); setCsvPreview(null); setCsvReport(null); } }}>
        <DialogContent className="max-w-[760px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> Matrícula em lote (CSV)</DialogTitle>
            <DialogDescription>
              {selectedCourse ? selectedCourse.title : ""} — envie uma planilha com as colunas <strong>nome</strong>, <strong>email</strong> e <strong>ceap_id</strong> (opcional).
            </DialogDescription>
          </DialogHeader>

          {/* Passo 1: escolher arquivo / modelo */}
          {!csvReport && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={downloadCsvTemplate} data-testid="csv-download-template">
                  <Download className="w-4 h-4 mr-1" /> Baixar modelo CSV
                </Button>
                <label className="inline-flex">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={onCsvFileChange}
                    data-testid="csv-file-input"
                  />
                  <span className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--border))] bg-background px-3 py-1.5 text-sm font-medium cursor-pointer hover:bg-accent transition-colors">
                    <Upload className="w-4 h-4" /> {csvFile ? "Trocar arquivo" : "Selecionar arquivo"}
                  </span>
                </label>
                {csvFile && <span className="text-xs text-muted-foreground truncate max-w-[220px]">{csvFile.name}</span>}
              </div>

              {csvLoading && (
                <div className="py-8 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /><p className="mt-2 text-sm">Analisando planilha…</p></div>
              )}

              {csvPreview && !csvLoading && (
                <>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge className="bg-sky-50 text-sky-800 border border-sky-200">Vincular: {csvPreview.summary.to_link}</Badge>
                    <Badge className="bg-violet-50 text-violet-800 border border-violet-200">Criar: {csvPreview.summary.new_user}</Badge>
                    <Badge className="bg-slate-100 text-slate-700 border border-slate-200">Já matriculados: {csvPreview.summary.already_enrolled}</Badge>
                    <Badge className="bg-rose-50 text-rose-700 border border-rose-200">Erros: {csvPreview.summary.error}</Badge>
                  </div>
                  <div className="rounded-lg border border-[hsl(var(--border))] max-h-[42vh] overflow-auto" data-testid="csv-preview-table">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">#</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>E-mail</TableHead>
                          <TableHead>CEAP ID</TableHead>
                          <TableHead>Situação</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {csvPreview.rows.map((r) => (
                          <TableRow key={`prev-${r.row}`}>
                            <TableCell className="text-xs text-muted-foreground">{r.row}</TableCell>
                            <TableCell className="text-sm">{r.nome || r.matched_name || "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{r.email || r.matched_email || "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{r.ceap_id || "—"}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                {csvStatusBadge(r.status)}
                                <span className="text-[11px] text-muted-foreground">{r.message}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 rounded-md p-2">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Alunos novos serão criados com uma senha temporária (exibida no relatório). Linhas com erro são ignoradas.</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Passo 2: relatório final */}
          {csvReport && (
            <div className="space-y-3" data-testid="csv-report">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" />Vinculados: {csvReport.linked}</Badge>
                <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" />Criados: {csvReport.created}</Badge>
                <Badge className="bg-slate-100 text-slate-700 border border-slate-200">Já matriculados: {csvReport.already}</Badge>
                <Badge className="bg-rose-50 text-rose-700 border border-rose-200"><AlertCircle className="w-3 h-3 mr-1" />Erros: {csvReport.errors}</Badge>
              </div>

              {csvReport.temp_passwords?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2">
                  <p className="text-sm font-medium flex items-center gap-1"><KeyRound className="w-4 h-4" /> Senhas temporárias dos alunos criados</p>
                  <p className="text-xs text-muted-foreground">Anote e repasse. Elas só aparecem agora.</p>
                  <div className="max-h-48 overflow-auto space-y-1">
                    {csvReport.temp_passwords.map((t, i) => (
                      <div key={`tp-${i}`} className="flex items-center justify-between text-xs bg-background rounded border border-[hsl(var(--border))] px-2 py-1">
                        <span className="truncate mr-2">{t.nome} · {t.email} · {t.ceap_id}</span>
                        <span className="font-mono">{t.password}</span>
                      </div>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => {
                    const txt = csvReport.temp_passwords.map((t) => `${t.nome}; ${t.email}; ${t.ceap_id}; ${t.password}`).join("\n");
                    navigator.clipboard?.writeText(txt); toast.success("Senhas copiadas");
                  }}>
                    <Copy className="w-4 h-4 mr-1" /> Copiar todas
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!csvReport ? (
              <>
                <Button variant="ghost" onClick={() => setCsvOpen(false)}>Cancelar</Button>
                <Button
                  onClick={doCsvCommit}
                  disabled={!csvPreview || csvCommitting || (csvPreview && (csvPreview.summary.to_link + csvPreview.summary.new_user) === 0)}
                  data-testid="csv-commit-btn"
                >
                  {csvCommitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
                  Confirmar matrícula
                </Button>
              </>
            ) : (
              <Button onClick={() => setCsvOpen(false)} data-testid="csv-report-close">Concluir</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
